'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/shared/lib/supabase/client'
import { Button } from '@/shared/components/Button'
import { Input } from '@/shared/components/Input'
import { Card, CardHeader, CardContent } from '@/shared/components/Card'
import { appIdFromNext, sanitizeNext } from '@/shared/lib/signupNext'
import { authErrorMessage } from '@/shared/lib/authError'
import { track } from '@/shared/lib/analytics'
import { isStandaloneDisplay } from '@/shared/lib/pwaDisplay'
import { getAppById } from '@/registry/apps'

type Mode = 'login' | 'signup'

// The emailed OTP length is set on the Supabase project (auth `otp_length`),
// not here — and it has drifted before (6 → 8). Don't hardcode an exact count:
// accept anything in Supabase's allowed range (6–10) and let the server be the
// source of truth on validity, so a future length change can't lock users out.
const MIN_CODE_LENGTH = 6
const MAX_CODE_LENGTH = 10

/**
 * Combined sign-in / sign-up screen.
 *
 * Both flows are email-first: the primary path emails a one-time code + one-tap
 * link (same OTP — either works once). The code entry is what lets an installed
 * PWA finish auth in its own window, since the emailed link would open in the
 * browser instead. Password is a secondary option behind a toggle.
 *
 * Sign-in and sign-up live in one component, and the "Sign up" / "Sign in"
 * cross-links flip `mode` in place rather than navigating to the other route.
 * That's deliberate: a PWA's manifest scope (/dashboard, /apps/<id>) doesn't
 * cover /login or /signup, so navigating between them would eject the user into
 * the browser — exactly the trap the middleware's in-scope login rewrite avoids.
 * Toggling in place keeps the whole auth flow inside the standalone window.
 * `/login` and `/signup` stay as thin routes that just pick the initial mode
 * (for marketing/creator deep-links and direct visits).
 */
export function AuthForm({ initialMode }: { initialMode: Mode }) {
  const [mode, setMode] = useState<Mode>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [sending, setSending] = useState(false)
  const [codeSent, setCodeSent] = useState(false)
  const [resent, setResent] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [usePassword, setUsePassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const supabase = createClient()

  const isSignup = mode === 'signup'

  // Read ?next= on the client (avoids a useSearchParams Suspense boundary).
  // When the middleware rewrites this screen onto an in-scope URL (so an
  // installed PWA stays in its own window), there's no ?next — the current path
  // IS where the user was headed, so fall back to it. Ignore the auth routes.
  // For sign-up, an app's path (e.g. /apps/sharon) also scopes the brand-new
  // account to just that app. Populated in an effect (not a useState
  // initializer) so the first client render matches the server's null —
  // otherwise React refuses to patch the mismatched markup.
  const [next, setNext] = useState<string | null>(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const explicitNext = sanitizeNext(params.get('next'))
    const path = window.location.pathname
    const pathNext =
      path !== '/login' && path !== '/signup' ? sanitizeNext(path) : null
    setNext(explicitNext ?? pathNext)
    // /auth/confirm bounces used/expired email links here with this hint.
    if (params.get('error') === 'link_expired') {
      setNotice('That sign-in link has expired or was already used. Enter your email below and we’ll send you a fresh one.')
    }
    // How did the user land on the auth screen? `in_app_rewrite` means the
    // middleware rendered login in place at an auth-gated URL (a PWA launch /
    // navigation while signed out) — the forced re-auth we're trying to kill.
    // `redirect` is a ?next= bounce; `direct` is a plain /login or /signup
    // visit. Pair with `signed_out` to tell forced re-logins from real ones.
    track('login_screen_viewed', {
      via: pathNext ? 'in_app_rewrite' : explicitNext ? 'redirect' : 'direct',
      standalone: isStandaloneDisplay(),
      mode: initialMode,
    })
  }, [initialMode])

  const soloAppId = appIdFromNext(next)
  const soloApp = soloAppId ? getAppById(soloAppId) : undefined
  // Stamp the originating app onto a new account's metadata (persisted on the
  // user row), translated into module preferences the first time they reach the
  // dashboard — so app-scoping survives even when they confirm via the emailed
  // link later, independent of `next`.
  const metadata = soloAppId ? { data: { signup_app: soloAppId } } : {}

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!email.trim()) return
    setError('')
    setNotice('')
    setSending(true)
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: isSignup, ...(isSignup ? metadata : {}) },
    })
    setSending(false)
    if (error) {
      // On sign-in, shouldCreateUser:false surfaces unknown emails as a signups
      // error — point them at sign-up instead of showing the raw message.
      setError(
        !isSignup && /signups not allowed/i.test(error.message)
          ? 'We couldn’t find an account for that email. Double-check it, or sign up below.'
          : authErrorMessage(error),
      )
      return
    }
    setResent(codeSent)
    setCodeSent(true)
  }

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setVerifying(true)
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: code.trim(),
      type: 'email',
    })
    if (error) {
      setError(
        /invalid|expired/i.test(error.message)
          ? 'That code didn’t match — check for typos, or resend a fresh one.'
          : authErrorMessage(error),
      )
      setVerifying(false)
      return
    }
    track('signed_in', { method: 'otp', mode, standalone: isStandaloneDisplay() })
    router.push(next ?? '/dashboard')
    router.refresh()
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (isSignup) {
      if (password !== confirmPassword) {
        setError('Passwords do not match')
        return
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters')
        return
      }
    }

    setLoading(true)

    const { error } = isSignup
      ? await supabase.auth.signUp({
          email,
          password,
          ...(soloAppId ? { options: { data: { signup_app: soloAppId } } } : {}),
        })
      : await supabase.auth.signInWithPassword({ email, password })

    if (error) {
      setError(authErrorMessage(error))
      setLoading(false)
      return
    }

    track('signed_in', { method: 'password', mode, standalone: isStandaloneDisplay() })
    router.push(next ?? '/dashboard')
    router.refresh()
  }

  // Clear the transient flow state when switching mode or starting over. Keep
  // the typed email — it's the same value the user wants in either mode.
  const resetFlow = () => {
    setCodeSent(false)
    setResent(false)
    setCode('')
    setError('')
    setNotice('')
  }

  const toggleMode = () => {
    resetFlow()
    setPassword('')
    setConfirmPassword('')
    setUsePassword(false)
    setMode(isSignup ? 'login' : 'signup')
  }

  const submitVerb = isSignup ? 'Create Account' : 'Sign In'
  const submittingVerb = isSignup ? 'Creating account...' : 'Signing in...'
  const codeNoun = isSignup ? 'sign-up code' : 'sign-in code'

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <img
            src="/icons/family-apps-192.png"
            alt="Peter's Apps"
            className="w-16 h-16 rounded-full mx-auto mb-3"
          />
          <h1 className="text-2xl font-bold text-center text-gray-900">
            {isSignup ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-center text-gray-600 mt-1">
            {isSignup
              ? soloApp
                ? `Sign up to start using ${soloApp.name}`
                : "Sign up to start using Peter's Apps"
              : "Sign in to access Peter's Apps"}
          </p>
        </CardHeader>
        <CardContent>
          {notice && (
            <p className="mb-4 text-sm text-amber-700 bg-amber-50 p-3 rounded-lg">
              {notice}
            </p>
          )}
          {isSignup && soloApp && (
            <p className="mb-4 text-sm text-gray-600 bg-gray-50 border border-gray-200 p-3 rounded-lg">
              Your account will start with just <strong>{soloApp.name}</strong>.
              You can turn on the other apps anytime in Settings.
            </p>
          )}
          {usePassword ? (
            <>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <Input
                  id="email"
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
                <Input
                  id="password"
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isSignup ? 'Create a password' : 'Enter your password'}
                  required
                />
                {isSignup && (
                  <Input
                    id="confirmPassword"
                    type="password"
                    label="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                  />
                )}
                {error && (
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                    {error}
                  </p>
                )}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? submittingVerb : submitVerb}
                </Button>
              </form>
              <button
                type="button"
                onClick={() => {
                  setUsePassword(false)
                  setError('')
                }}
                className="mt-3 w-full text-center text-sm text-blue-600 hover:underline"
              >
                Email me a {codeNoun} instead
              </button>
            </>
          ) : codeSent ? (
            <>
              <p className="text-sm text-emerald-600 bg-emerald-50 p-3 rounded-lg">
                {resent ? 'New code sent' : 'Check your email'} — we sent a
                code to <span className="font-medium">{email.trim()}</span>.
                Enter it below, or just tap the link in the email.
              </p>
              <form onSubmit={verifyCode} className="mt-4 space-y-4">
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={MAX_CODE_LENGTH}
                  label={isSignup ? 'Sign-up code' : 'Sign-in code'}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="12345678"
                  required
                />
                {error && (
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={verifying || code.trim().length < MIN_CODE_LENGTH}
                >
                  {verifying ? submittingVerb : submitVerb}
                </Button>
              </form>
              <div className="mt-3 flex justify-center gap-4 text-sm">
                <button
                  type="button"
                  onClick={() => sendCode()}
                  disabled={sending}
                  className="text-blue-600 hover:underline disabled:opacity-50"
                >
                  {sending ? 'Sending…' : 'Resend code'}
                </button>
                <button
                  type="button"
                  onClick={resetFlow}
                  className="text-gray-500 hover:underline"
                >
                  Use a different email
                </button>
              </div>
            </>
          ) : (
            <>
              <form onSubmit={sendCode} className="space-y-4">
                <Input
                  id="email"
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
                {error && (
                  <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                    {error}
                  </p>
                )}
                <Button type="submit" className="w-full" disabled={sending}>
                  {sending ? 'Sending…' : `Email me a ${codeNoun}`}
                </Button>
              </form>
              <p className="mt-2 text-center text-xs text-gray-500">
                No password needed — we’ll email you a code and a one-tap link.
              </p>
              <button
                type="button"
                onClick={() => {
                  setUsePassword(true)
                  setError('')
                }}
                className="mt-3 w-full text-center text-sm text-blue-600 hover:underline"
              >
                {isSignup
                  ? 'Sign up with a password instead'
                  : 'Sign in with a password instead'}
              </button>
            </>
          )}
          <p className="mt-4 text-center text-sm text-gray-600">
            {isSignup ? 'Already have an account? ' : "Don't have an account? "}
            <button
              type="button"
              onClick={toggleMode}
              className="text-blue-600 hover:underline"
            >
              {isSignup ? 'Sign in' : 'Sign up'}
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
